import { useCallback, useEffect, useState } from 'react';

import { useMst } from './models/rootModel';

import { observer } from 'mobx-react-lite';
import { Route, Routes } from 'react-router-dom';
import { StyleProvider } from './components/common/StyleProvider';
import { useUserLocation } from './hooks/useUserLocation.hook';
import { ErrorType, recordError } from './models/apiWrappers/api/exceptions';
import Onboarding from './Onboarding';
import { initGrowthBook } from './providers/featureFlag/growthbook';
import { PaymentSummary } from './screens/PaymentSummary';
import { useAnalytics } from './utils/analytics';
import { changeBrazeUser, initBraze, resetBrazeUser } from './utils/braze';
import { FALL_BACK_DOCUMENT } from './utils/consts';
import { initTapfiliate } from './utils/tapfiliate';
import { getDocumentId } from './utils/utilsFunctions';
import { RoutesNames } from './routes';

initTapfiliate();
initBraze();

const App = observer(() => {
  const { Step, UserDiscoveryResponse, UserProgress, UserPayment } = useMst();
  const analytics = useAnalytics();
  const { setAnonymousId, anonymousId } = UserDiscoveryResponse;

  const queryString = window.location.search;
  const searchParams = new URLSearchParams(window.location.search);
  const accessToken = window.localStorage.getItem('authToken') || searchParams.get('accessToken');
  const backUrl = searchParams.get('backUrl');
  const planId = searchParams.get('plan');
  const klarnaOnlySearchParam = searchParams.get('klarnaOnly');
  const quoraClickId = searchParams.get('qclid');
  const anonymousIdQueryParam = searchParams.get('anonymousId');
  const [isLoadingData, setIsLoadingData] = useState(false);

  useUserLocation();

  // we want to allow users to go back on first step (by browser back button)
  // but not on the last steps
  const handleBackButton = useCallback(() => {
    if (UserProgress.isFirstStep) {
      window.history.back();
      return;
    }

    window.history.go(1); // prevents to go back
  }, [UserProgress.isFirstStep]);

  useEffect(() => {
    const dataLoader = async () => {
      if (Step.steps.length > 0) return;

      const promises = [
        ...(anonymousIdQueryParam ? [] : [analytics.setAnalyticsCookieIfNeeded()]),
        Step.getSteps(getDocumentId() || FALL_BACK_DOCUMENT),
        UserPayment.loadPaymentMetaIfNeed(planId),
        initGrowthBook(analytics.track),
      ];

      const result = await Promise.all(promises);

      if (anonymousIdQueryParam) {
        setAnonymousId(anonymousIdQueryParam);
        return;
      }

      const segmentAnonymousId = result[0];

      if (segmentAnonymousId) {
        setAnonymousId(segmentAnonymousId);
      }
    };

    setIsLoadingData(true);
    dataLoader()
      .catch((error) => recordError(ErrorType.HANDLED, error))
      .finally(() => {
        setIsLoadingData(false);
      });
  }, [Step, UserPayment, analytics, planId, setAnonymousId, anonymousIdQueryParam]);

  useEffect(() => {
    if (accessToken) {
      UserDiscoveryResponse.setAuthToken(accessToken);
      UserDiscoveryResponse.getUser();
    }
  }, [UserDiscoveryResponse, accessToken]);

  useEffect(() => {
    if (planId) {
      UserPayment.setHasPlanSearchParam(true);
    }
  }, [UserPayment, planId]);

  useEffect(() => {
    if (klarnaOnlySearchParam) {
      if (klarnaOnlySearchParam === 'true') {
        UserPayment.setKlarnaOnly(true);
      }

      if (klarnaOnlySearchParam === 'false') {
        UserPayment.setKlarnaOnly(false);
      }
    }
  }, [UserPayment, klarnaOnlySearchParam]);

  

  useEffect(() => {
    // 'blank' is a default value when there was not qclid
    if (quoraClickId && quoraClickId !== 'blank') {
      UserDiscoveryResponse.setQuoraClickId(quoraClickId);
    }
  }, [UserDiscoveryResponse, quoraClickId]);

  useEffect(() => {
    backUrl && UserProgress.setBackUrl(backUrl);
  }, [UserProgress, backUrl]);

  useEffect(() => {
    Step.applyCoupon ? UserPayment.applyCustomCouponCode(Step.applyCoupon) : UserPayment.getDefaultCouponCode();
  }, [Step.applyCoupon, UserPayment]);

  useEffect(() => {
    if (queryString) {
      UserDiscoveryResponse.setUtms(queryString ? queryString.substring(1) : window.document.referrer.split('/?')[1]);
    }
  }, [queryString, UserDiscoveryResponse]);

  useEffect(() => {
    resetBrazeUser();
  }, []);

  useEffect(() => {
    if (UserDiscoveryResponse.accountId) {
      analytics.identify(UserDiscoveryResponse.accountId);
      changeBrazeUser(UserDiscoveryResponse.accountId);
      return;
    }

    if (anonymousIdQueryParam) {
      analytics.setAnonymousId(anonymousIdQueryParam);
    }
  }, [UserDiscoveryResponse.accountId, analytics, anonymousIdQueryParam]);

  useEffect(() => {
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handleBackButton);

    return () => {
      return window.removeEventListener('popstate', handleBackButton);
    };
  }, [handleBackButton]);

  return (
    <StyleProvider>
      <Routes>
        <Route
          //FIXME: apply feature flag providers if needed
          path={`/document/:documentId/${RoutesNames.PaymentConfirmation}`}
          element={<PaymentSummary accessToken={UserDiscoveryResponse.authToken} />}
        />
        <Route
          path="/document/:documentId"
          element={<Onboarding isLoadingData={isLoadingData} anonymousId={anonymousId ?? undefined} />}
        />
      </Routes>
    </StyleProvider>
  );
});

export default App;
